import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';
import * as Yup from 'yup';
import { firebaseAuth } from '../../../config/firebase';
import { useAuth } from '../../../hooks/auth';
import {
  EmailInput,
  FuzzyLogoNewOrange,
  WarningIcon,
} from '../../../assets/svg/SVGComponents';
import Input from '../../../components/Input';
import PrimaryButton from '../../../components/Buttons/PrimaryButton';
import getValidationErrors from '../../../utils/getValidationErrors';

import { Container, Content, StatusMessage, ErrorMessage } from './styles';

type PageStatus = 'verifying' | 'needs-email' | 'error';

const MagicLink: React.FC = () => {
  const navigate = useNavigate();
  const { verifyMagicLink } = useAuth();
  const formRef = useRef<FormHandles>(null);

  const [status, setStatus] = useState<PageStatus>('verifying');
  const [isLoading, setIsLoading] = useState(false);

  const verify = async (email: string) => {
    setIsLoading(true);
    try {
      await verifyMagicLink(email);
      navigate('/home', { replace: true });
    } catch {
      setStatus('error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!firebaseAuth.isSignInWithEmailLink(window.location.href)) {
      navigate('/', { replace: true });
      return;
    }

    const savedEmail = localStorage.getItem('fuzzy:magicLinkEmail');
    if (savedEmail) {
      verify(savedEmail);
    } else {
      setStatus('needs-email');
    }
    // navigate e verify são estáveis na montagem; dependências intencionalmente omitidas
  }, []);

  const handleEmailSubmit = async (data: { email: string }) => {
    formRef.current?.setErrors({});
    try {
      const schema = Yup.object().shape({
        email: Yup.string()
          .required('O email é obrigatório.')
          .email('Informe um email válido.'),
      });
      await schema.validate(data, { abortEarly: false });
      await verify(data.email);
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const errors = getValidationErrors(err);
        formRef.current?.setErrors(errors);
      }
    }
  };

  if (status === 'verifying') {
    return (
      <Container>
        <Content>
          <FuzzyLogoNewOrange />
          <StatusMessage>
            <div className="spinner" />
            <p>Verificando link de acesso...</p>
          </StatusMessage>
        </Content>
      </Container>
    );
  }

  if (status === 'error') {
    return (
      <Container>
        <Content>
          <FuzzyLogoNewOrange />
          <ErrorMessage>
            <span className="error-icon">
              <WarningIcon width="6rem" strokeWidth="10" stroke="#ff9800" />
            </span>
            <h2>Link inválido ou expirado</h2>
            <p>
              O link de acesso é inválido ou já expirou. Solicite um novo link
              na página de login.
            </p>
            <button
              type="button"
              onClick={() => navigate('/', { replace: true })}
            >
              Voltar ao login
            </button>
          </ErrorMessage>
        </Content>
      </Container>
    );
  }

  return (
    <Container>
      <Content>
        <FuzzyLogoNewOrange />
        <Form ref={formRef} onSubmit={handleEmailSubmit}>
          <h1>Confirmar email</h1>
          <p className="description">
            Por segurança, confirme o email utilizado para solicitar o link de
            acesso.
          </p>
          <span>Email</span>
          <Input
            name="email"
            type="text"
            placeholder="seu@email.com"
            icon={EmailInput}
            autoComplete="email"
            autoFocus
          />
          <PrimaryButton type="submit" loading={isLoading}>
            Confirmar e entrar
          </PrimaryButton>
        </Form>
      </Content>
    </Container>
  );
};

export default MagicLink;
